/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useEffect, useMemo, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppLoader from "components/AppLoader";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";

function Application() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [error, setError] = useState(null);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);
  const [selectedStatusApplication, setSelectedStatusApplication] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [createItem, setCreateItem] = useState({
    name: "",
    title: "",
    description: "",
    orgId: 1,
    isActive: true,
  });
  const [editItem, setEditItem] = useState({
    id: 0,
    name: "",
    title: "",
    description: "",
    orgId: 0,
    isActive: true,
  });

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const parseBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "active";
    }
    return false;
  };

  const getApplicationId = (item) =>
    item?.applicationId ?? item?.ApplicationId ?? item?.id ?? item?.Id ?? 0;

  const getName = (item) => item?.name ?? item?.Name ?? "";
  const getTitle = (item) => item?.title ?? item?.Title ?? "";
  const getDescription = (item) => item?.description ?? item?.Description ?? "";
  const getOrgId = (item) => item?.orgId ?? item?.OrgId ?? 0;
  const getOrgName = (item) => item?.orgName ?? item?.OrgName ?? "";

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.getAllApplications;
      if (!endpoint) {
        throw new Error("Application endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to load applications.");
      }

      const data = await response.json();
      setApplications(normalizeArray(data));
    } catch (e) {
      setError(e.message || "Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleOpenCreate = () => {
    const fallbackOrgId = applications.length > 0 ? getOrgId(applications[0]) : 1;
    setCreateItem({
      name: "",
      title: "",
      description: "",
      orgId: fallbackOrgId > 0 ? fallbackOrgId : 1,
      isActive: true,
    });
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
  };

  const handleCreateChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateItem((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveCreate = async () => {
    if (!createItem.name.trim() || !createItem.title.trim() || !createItem.description.trim()) {
      setError("Name, title and description are required.");
      return;
    }

    const parsedOrgId = Number(createItem.orgId);
    if (!Number.isFinite(parsedOrgId) || parsedOrgId <= 0) {
      setError("A valid Organization Id is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.createApp;
      if (!endpoint) {
        throw new Error("Create application endpoint is not configured.");
      }

      const params = new URLSearchParams({
        name: createItem.name.trim(),
        description: createItem.description.trim(),
        title: createItem.title.trim(),
        orgId: String(parsedOrgId),
        isActive: createItem.isActive ? "1" : "0",
      });

      const url = `${appSettings.baseUrl}${endpoint}?${params.toString()}`;
      const response = await fetchWithAuth(url, { method: "POST" });

      if (!response.ok) {
        let errorMessage = "Failed to create application.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parsing errors and use default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseCreate();
      await fetchApplications();
    } catch (e) {
      setError(e.message || "Failed to create application.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (item) => {
    setEditItem({
      id: getApplicationId(item),
      name: getName(item),
      title: getTitle(item),
      description: getDescription(item),
      orgId: getOrgId(item),
      isActive: parseBool(item?.isActive ?? item?.IsActive),
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditItem({
      id: 0,
      name: "",
      title: "",
      description: "",
      orgId: 0,
      isActive: true,
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditItem((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editItem.name.trim() || !editItem.title.trim() || !editItem.description.trim()) {
      setError("Name, title and description are required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateApplication;
      if (!endpoint) {
        throw new Error("Update application endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          id: editItem.id,
          name: editItem.name,
          title: editItem.title,
          description: editItem.description,
          orgId: Number(editItem.orgId),
          isActive: editItem.isActive ? 1 : 0,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update application.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parsing errors and use default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseEdit();
      await fetchApplications();
    } catch (e) {
      setError(e.message || "Failed to update application.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleApplicationStatus = async (item) => {
    const applicationId = getApplicationId(item);
    if (!applicationId) {
      setError("Application id is missing.");
      return;
    }

    const isCurrentlyActive = parseBool(item?.isActive ?? item?.IsActive);
    const nextIsActive = !isCurrentlyActive;
    const actionLabel = nextIsActive ? "activate" : "deactivate";

    setStatusUpdatingId(applicationId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateApplication;
      if (!endpoint) {
        throw new Error("Update application endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          id: applicationId,
          name: getName(item),
          title: getTitle(item),
          description: getDescription(item),
          orgId: Number(getOrgId(item)),
          isActive: nextIsActive ? 1 : 0,
        }),
      });

      if (!response.ok) {
        let errorMessage = `Failed to ${actionLabel} application.`;
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Keep default message when response is not JSON.
        }
        throw new Error(errorMessage);
      }

      await fetchApplications();
    } catch (e) {
      setError(e.message || `Failed to ${actionLabel} application.`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleOpenStatusConfirm = (item) => {
    const applicationId = getApplicationId(item);
    if (!applicationId) {
      setError("Application id is missing.");
      return;
    }

    const latest =
      applications.find((x) => String(getApplicationId(x)) === String(applicationId)) || item;
    setSelectedStatusApplication(latest);
    setOpenStatusConfirm(true);
  };

  const handleCloseStatusConfirm = () => {
    if (statusUpdatingId !== null) return;
    setOpenStatusConfirm(false);
    setSelectedStatusApplication(null);
  };

  const handleConfirmToggleStatus = async () => {
    if (!selectedStatusApplication) return;
    await handleToggleApplicationStatus(selectedStatusApplication);
    setOpenStatusConfirm(false);
    setSelectedStatusApplication(null);
  };

  const tableData = useMemo(() => {
    const columns = [
      { Header: "name", accessor: "name", align: "left" },
      { Header: "title", accessor: "title", align: "left" },
      { Header: "description", accessor: "description", align: "left" },
      { Header: "organization", accessor: "organization", align: "left" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const rows = applications.map((item) => {
      const isActive = parseBool(item?.isActive ?? item?.IsActive);
      return {
        name: (
          <MDTypography variant="button" fontWeight="medium">
            {getName(item)}
          </MDTypography>
        ),
        title: (
          <MDTypography variant="caption" color="text">
            {getTitle(item)}
          </MDTypography>
        ),
        description: (
          <MDTypography variant="caption" color="text">
            {getDescription(item)}
          </MDTypography>
        ),
        organization: (
          <MDTypography variant="caption" color="text">
            {getOrgName(item) || `Org ${getOrgId(item)}`}
          </MDTypography>
        ),
        status: (
          <MDBadge
            badgeContent={isActive ? "active" : "inactive"}
            color={isActive ? "success" : "secondary"}
            variant="gradient"
            size="sm"
          />
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <MDButton
              variant="text"
              color="info"
              size="small"
              onClick={() => handleOpenEdit(item)}
              disabled={String(statusUpdatingId) === String(getApplicationId(item))}
            >
              Edit
            </MDButton>
            <Tooltip title={isActive ? "Deactivate Application" : "Activate Application"}>
              <span>
                <IconButton
                  color={isActive ? "error" : "success"}
                  size="small"
                  onClick={() => handleOpenStatusConfirm(item)}
                  disabled={String(statusUpdatingId) === String(getApplicationId(item))}
                >
                  <Icon fontSize="small">{isActive ? "toggle_off" : "toggle_on"}</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </MDBox>
        ),
      };
    });

    return { columns, rows };
  }, [applications, statusUpdatingId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Applications
                </MDTypography>
                <MDBox display="flex" gap={1}>
                  <MDButton variant="gradient" color="success" onClick={handleOpenCreate}>
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;add application
                  </MDButton>
                  <MDButton variant="gradient" color="success" onClick={fetchApplications}>
                    <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>
                    &nbsp;refresh
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        mx={2}
                        mb={2}
                        py={1}
                        px={2}
                        variant="gradient"
                        sx={{ backgroundColor: "lightcoral" }}
                        borderRadius="lg"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {error}
                        </MDTypography>
                        <MDTypography
                          color="white"
                          sx={{ cursor: "pointer", ml: 2, lineHeight: 0 }}
                          onClick={() => setError(null)}
                        >
                          x
                        </MDTypography>
                      </MDBox>
                    )}

                    <DataTable
                      table={tableData}
                      isSorted
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                      showTotalEntries
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openCreate} onClose={handleCloseCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Application</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={createItem.name}
              onChange={handleCreateChange}
            />
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={createItem.title}
              onChange={handleCreateChange}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={createItem.description}
              onChange={handleCreateChange}
              multiline
              minRows={3}
            />
            <TextField
              fullWidth
              label="Organization Id"
              name="orgId"
              type="number"
              value={createItem.orgId}
              onChange={handleCreateChange}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createItem.isActive}
                  onChange={handleCreateChange}
                  name="isActive"
                />
              }
              label="Active"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseCreate} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSaveCreate} color="info" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Application</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={editItem.name}
              onChange={handleEditChange}
            />
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={editItem.title}
              onChange={handleEditChange}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={editItem.description}
              onChange={handleEditChange}
              multiline
              minRows={3}
            />
            <FormControlLabel
              control={
                <Switch checked={editItem.isActive} onChange={handleEditChange} name="isActive" />
              }
              label="Active"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseEdit} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSaveEdit} color="info" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openStatusConfirm} onClose={handleCloseStatusConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to{" "}
            {parseBool(selectedStatusApplication?.isActive ?? selectedStatusApplication?.IsActive)
              ? "deactivate"
              : "activate"}{" "}
            this application?
          </MDTypography>
          {selectedStatusApplication && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {getName(selectedStatusApplication) || "-"}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseStatusConfirm} color="secondary" disabled={saving}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmToggleStatus}
            variant="gradient"
            color={
              parseBool(selectedStatusApplication?.isActive ?? selectedStatusApplication?.IsActive)
                ? "error"
                : "success"
            }
            disabled={statusUpdatingId !== null}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Application;
